import { inject, Injectable } from '@angular/core';
import {
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  forkJoin,
  map,
  mergeMap,
  Observable,
  of,
  shareReplay,
  switchMap,
} from 'rxjs';
import {
  Calendar as MeetingsCalendar,
  CalendarType as MeetingsCalendarType,
  HostService,
  MeetingType,
  newBusinessCenterApplicationContextProperties,
  newPartnerCenterApplicationContextProperties,
  Service,
} from '@vendasta/meetings';
import { Association, CalendarType, Calendar, CalendarWithMeetingTypes } from '../interfaces/config.interface';
import { SalespersonService, SalesTeamService } from '@vendasta/sales';

import { ACCOUNT_GROUP_ID_TOKEN, CURRENT_USER_ID_TOKEN, PARTNER_ID_TOKEN } from '../tokens';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class BookingConfigService {
  private readonly currentUserId$ = inject(CURRENT_USER_ID_TOKEN);
  private readonly partnerId$ = inject(PARTNER_ID_TOKEN);
  private readonly accountGroupId$ = inject(ACCOUNT_GROUP_ID_TOKEN);

  private readonly meetingService = inject(HostService);
  private readonly salespersonService = inject(SalespersonService);
  private readonly salesTeamService = inject(SalesTeamService);
  private readonly translateService = inject(TranslateService);

  private readonly partnerIdAndAccountGroupId$ = combineLatest([this.partnerId$, this.accountGroupId$]).pipe(
    distinctUntilChanged((a, b) => a[0] === b[0] && a[1] === b[1]),
  );

  private readonly salesPersonId$ = combineLatest([this.partnerId$, this.currentUserId$]).pipe(
    switchMap(([partnerId, userId]) => this.salespersonService.getSalespersonByUserId(partnerId, userId)),
    catchError(() => of(undefined)),
    map((salesperson) => salesperson?.salespersonId || ''),
    shareReplay(1),
  );

  private readonly salesTeamIDs$ = this.salesPersonId$.pipe(
    switchMap((id) => (id ? this.salesTeamService.getSalesTeamsBySalesperson(id) : of([]))),
    catchError(() => of([])),
    map((teams) => teams.map((team) => team?.groupId).filter((groupId): groupId is string => groupId !== undefined)),
  );

  private readonly hostId$ = combineLatest([this.currentUserId$, this.partnerIdAndAccountGroupId$]).pipe(
    switchMap(([currentUserId, [partnerId, accountGroupId]]) => {
      if (accountGroupId) {
        return this.meetingService.buildHostId({
          userId: currentUserId,
          applicationContextProperties: newBusinessCenterApplicationContextProperties({ business_id: accountGroupId }),
        });
      }
      return this.salesPersonId$.pipe(
        filter((salespersonId) => !!salespersonId),
        switchMap(() =>
          this.meetingService.buildHostId({
            applicationContextProperties: newPartnerCenterApplicationContextProperties({ partner_id: partnerId }),
            userId: currentUserId,
          }),
        ),
      );
    }),
    catchError((error) => {
      console.warn(`Failed to fetch hostId:`, error);
      return '';
    }),
    shareReplay(1),
  );

  private readonly serviceCalendars$: Observable<Calendar[]> = this.hostId$.pipe(
    switchMap((hostId) =>
      this.meetingService.listServices({
        hostId: hostId,
      }),
    ),
    map((serviceCalendars: Service[]) => {
      const calendars: Calendar[] = [];
      serviceCalendars.forEach((service) => {
        const calendar: Calendar = {
          id: service.id,
          name: service.name,
          slug: service.slug,
          icon: 'menu_book',
          calendarType: CalendarType.SERVICE,
          associations: [],
        };
        calendars.push(calendar);
      });
      return calendars;
    }),
    shareReplay(1),
  );

  private readonly calendarsThatAreAggregates$ = this.serviceCalendars$;

  private readonly personalCalendar$ = combineLatest([this.partnerIdAndAccountGroupId$, this.currentUserId$]).pipe(
    switchMap(([[partnerId, accountGroupId], userId]) => {
      if (accountGroupId) {
        return this.meetingService.ensurePersonalCalendarExists({
          applicationContextProperties: newBusinessCenterApplicationContextProperties({ business_id: accountGroupId }),
          userId: userId,
        });
      } else {
        return this.salesPersonId$.pipe(
          filter((salespersonId) => !!salespersonId),
          switchMap(() =>
            this.meetingService.ensurePersonalCalendarExists({
              applicationContextProperties: newPartnerCenterApplicationContextProperties({ partner_id: partnerId }),
              userId: userId,
            }),
          ),
        );
      }
    }),
    switchMap((calendarId) =>
      this.meetingService.getCalendar({ calendarId: calendarId }).pipe(
        catchError((error) => {
          console.warn(`Failed to fetch personal calendar ${calendarId}:`, error);
          // Return a fallback calendar object for the personal calendar
          return of({
            id: calendarId,
            displayName: 'Personal Calendar (Unavailable)',
            calendarType: MeetingsCalendarType.CALENDAR_TYPE_PERSONAL,
          });
        }),
      ),
    ),
  );

  private readonly groupCalendars$: Observable<MeetingsCalendar[]> = this.partnerIdAndAccountGroupId$.pipe(
    switchMap(([_, accountGroupId]) => {
      if (accountGroupId) {
        return this.meetingService.ensureGroupCalendarsExist({ externalIds: [accountGroupId] });
      } else {
        return this.salesTeamIDs$.pipe(
          switchMap((salesTeamIds) =>
            salesTeamIds.length === 0
              ? of([])
              : this.meetingService.ensureGroupCalendarsExist({
                  externalIds: salesTeamIds,
                }),
          ),
        );
      }
    }),
    mergeMap((calendarIds) => {
      if (calendarIds.length === 0) {
        return of([]);
      }
      return forkJoin(
        calendarIds.map((calendarId) =>
          this.meetingService.getCalendar({ calendarId }).pipe(
            catchError((error) => {
              console.warn(`Failed to fetch group calendar ${calendarId}:`, error);
              // Return a fallback calendar object for group calendars
              return of({
                id: calendarId,
                displayName: 'Group Calendar (Unavailable)',
                calendarType: MeetingsCalendarType.CALENDAR_TYPE_GROUP,
              } as MeetingsCalendar);
            }),
          ),
        ),
      );
    }),
  );

  private readonly calendarsThatUseMeetingTypes$ = combineLatest([this.personalCalendar$, this.groupCalendars$]).pipe(
    map(([personalCalendar, groupCalendars]) => [personalCalendar, ...groupCalendars]),
    shareReplay(1),
  );

  private readonly meetingTypes$ = this.calendarsThatUseMeetingTypes$.pipe(
    switchMap((calendars) => {
      const calendarIds = calendars
        .filter((calendar) => calendar.calendarType !== MeetingsCalendarType.CALENDAR_TYPE_INVALID)
        .map((calendar) => calendar.id);

      return this.meetingService
        .getMeetingTypesForCalendars({
          calendarIds: calendarIds,
          metadata: {},
        })
        .pipe(
          catchError((error) => {
            console.warn('Failed to fetch meeting types for calendars:', error);
            // Return empty meeting types when the API call fails
            return of({});
          }),
        );
    }),
  );

  getAllCalendars(): Observable<Calendar[]> {
    const calendarsThatUseMeetingTypes$ = combineLatest([this.calendarsThatUseMeetingTypes$, this.meetingTypes$]).pipe(
      map(([calendars, meetingTypes]) => {
        const calendarsWithAssociations: Calendar[] = [];

        calendars.forEach((calendar) => {
          const associations: Association[] =
            (meetingTypes as Record<string, MeetingType[]>)?.[calendar.id]
              ?.map((mt: MeetingType) => ({
                name: mt.name,
                meetingTypeId: mt.id,
                calendarId: calendar.id,
              }))
              .sort((a, b) => (a.name < b.name ? -1 : 1)) || [];

          const calendarWithAssociations: Calendar = {
            name:
              calendar.displayName ||
              this.getFallbackDisplayName(calendar.calendarType === MeetingsCalendarType.CALENDAR_TYPE_PERSONAL),
            id: calendar.id,
            calendarType: this.convertExternalCalendarTypeToInternal(calendar.calendarType),
            icon: calendar.calendarType === MeetingsCalendarType.CALENDAR_TYPE_PERSONAL ? 'person' : 'group',
            associations: associations,
          };

          calendarsWithAssociations.push(calendarWithAssociations);
        });

        return calendarsWithAssociations;
      }),
      shareReplay(1),
    );

    const aggregateCalendars$ = this.calendarsThatAreAggregates$;

    return combineLatest([calendarsThatUseMeetingTypes$, aggregateCalendars$]).pipe(
      map(([calendarsThatUseMeetingTypes, aggregateCalendars]) => [
        ...calendarsThatUseMeetingTypes,
        ...aggregateCalendars,
      ]),
    );
  }

  /**
   * @deprecated use getAllCalendars
   */
  getCalendarsWithTypes(): Observable<CalendarWithMeetingTypes[]> {
    return combineLatest([this.calendarsThatUseMeetingTypes$, this.meetingTypes$]).pipe(
      map(([calendars, events]) =>
        calendars
          .filter((calendar) => calendar.calendarType !== MeetingsCalendarType.CALENDAR_TYPE_INVALID)
          .map(
            (calendar) =>
              ({
                calendarName:
                  calendar.displayName ||
                  this.getFallbackDisplayName(calendar.calendarType === MeetingsCalendarType.CALENDAR_TYPE_PERSONAL),
                calendarId: calendar.id,
                meetingTypes:
                  (events as Record<string, MeetingType[]>)?.[calendar.id]?.map((mt: MeetingType) => ({
                    name: mt.name,
                    meetingTypeId: mt.id,
                    calendarId: calendar.id,
                  })) || [],
                isPersonal: calendar.calendarType === MeetingsCalendarType.CALENDAR_TYPE_PERSONAL,
              }) as CalendarWithMeetingTypes,
          )
          .sort((a, _) => (a.isPersonal ? -1 : 1)),
      ),
    );
  }

  private getFallbackDisplayName(isPersonal: boolean): string {
    if (isPersonal) {
      return this.translateService.instant('AI_ASSISTANT.SETTINGS.ENABLE_MEETING_BOOKING.PERSONAL');
    }
    return this.translateService.instant('AI_ASSISTANT.SETTINGS.ENABLE_MEETING_BOOKING.TEAM');
  }

  private convertExternalCalendarTypeToInternal(calendarType: MeetingsCalendarType): CalendarType {
    switch (calendarType) {
      case MeetingsCalendarType.CALENDAR_TYPE_GROUP:
        return CalendarType.GROUP;
      case MeetingsCalendarType.CALENDAR_TYPE_PERSONAL:
        return CalendarType.PERSONAL;
      default:
        return CalendarType.INVALID;
    }
  }
}
